import { Box, Flex, Stack, Text, useToken } from '@chakra-ui/react';
import type { Ref } from 'react';

const colorSchemes = {
  red: {
    primary: 'red.300',
    body: 'gray.600',
    stripes: 'red.300',
    lines: 'blackAlpha.300',
  },
  white: {
    primary: 'red.300',
    body: 'gray.50',
    stripes: 'gray.50',
    lines: 'whiteAlpha.400',
  },
};

interface ChartProps {
  colorScheme: 'red' | 'white';
  isScrolled?: boolean;
  intersectionRef: Ref<HTMLDivElement>;
}

export function Chart({
  colorScheme,
  isScrolled,
  intersectionRef,
}: ChartProps) {
  const xl = useToken('space', 'xl');
  const colors = colorSchemes[colorScheme];

  return (
    <Box className="chart">
      <Stack spacing="lg" justifyContent="flex-end">
        <Stack spacing="2xs">
          <Text color={colors.body} size="body2">
            Diet only
          </Text>
          <Box
            height={{ base: 'sm', lg: 'lg' }}
            width="14%"
            borderRadius="base"
            bgGradient={`linear(to-tr, ${colors.stripes} 10%, transparent 10%, transparent 50%, ${colors.stripes} 50%, ${colors.stripes} 60%, transparent 60%, transparent 100%)`}
            bgSize="6px 6px"
            transitionDuration="ultra-slow"
            transitionProperty="clip-path"
            clipPath={
              isScrolled
                ? 'polygon(0 0, 100% 0, 100% 100%, 0% 100%)'
                : 'polygon(0 0, 0 0, 0 100%, 0% 100%)'
            }
          />
        </Stack>
        <Stack spacing="2xs" ref={intersectionRef}>
          <Text color={colors.body} size="body2">
            500mg Mitopure&trade;
          </Text>
          <Box
            height={{ base: 'sm', lg: 'lg' }}
            width="94%"
            borderRadius="base"
            bgColor={colors.stripes}
            bgSize="6px 6px"
            transitionDuration="ultra-slow"
            transitionProperty="common"
            transform={isScrolled ? 'scaleX(1)' : 'scaleX(0)'}
            transformOrigin="left center"
          />
        </Stack>
      </Stack>
      <Flex
        top={{ base: '-sm', lg: 'm' }}
        width="100%"
        height={{ base: `calc(100% + ${xl})`, lg: '100%' }}
        position="absolute"
        justifyContent="space-between"
        flexWrap="wrap"
      >
        {Array.from({ length: 11 }).map((_, j) => (
          <Text
            key={j}
            display="block"
            as="span"
            size="overline"
            height="100%"
            pos="relative"
            borderLeftWidth={1}
            borderStyle="dashed"
            borderColor={colors.lines}
            _after={{
              color: colors.body,
              content: `"${(j * 100) / 2}"`,
              position: 'absolute',
              transform: 'translateX(-50%)',
              bottom: '-sm',
            }}
            sx={{
              '&:nth-of-type(even)': {
                display: 'none',
              },
            }}
          />
        ))}
        <Text
          color={colors.body}
          mt="lg"
          flexBasis="100%"
          size="overline"
          textAlign="center"
        >
          Urolithin A Glucoronide (ng/ml)
        </Text>
      </Flex>
    </Box>
  );
}
